<template>
  <table class="w-full">
    <thead>
      <tr>
        <th v-for="field in fields" :key="field.key"
            class="px-2 py-2 text-left text-xs font-medium text-[#42A9C2] uppercase tracking-wider align-top">
          {{ field.label }}
          <!-- El filtro va dentro de la celda para heredar su alineación -->
          <TableFilterControl
            v-if="definitionFor(field.key)"
            :definition="definitionFor(field.key)"
            :label="field.label"
            :model-value="filters[field.key]"
            @change="onFilterInput(field.key, $event)"
          />
        </th>
      </tr>
      <tr v-if="filterError">
        <th :colspan="fields.length" class="px-2 pb-2 text-left font-normal">
          <p class="text-xs text-red-600 normal-case">{{ filterError }}</p>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="(item, index) in items" :key="index"
          class="hover:bg-gray-50 cursor-pointer"
          @click="navigateToDetail(item.id)">
        <td v-for="field in fields" :key="field.key"
            class="px-2 py-2 text-sm text-gray-500 first:rounded-l-lg last:rounded-r-lg">
          <span
            v-if="field.key === 'contract_improvement' && item.contract_improvement_accepted_at"
            class="inline-flex rounded-full bg-[#E2F7FB] px-2 py-1 text-xs font-semibold text-[#147D98]"
          >
            {{ t('contractImprovement.badge') }}
          </span>
          <span v-else class="truncate block max-w-[260px]" :title="getDisplayValue(item, field.key)">
            {{ getDisplayValue(item, field.key) }}
          </span>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script setup>
import { defineProps } from 'vue';
import TableFilterControl from '@/components/table/TableFilterControl.vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const router = useRouter();
const { t } = useI18n();

const emit = defineEmits(['update:filters', 'filter-change']);

const props = defineProps({
  fields: {
    type: Array,
    required: true
  },
  filterDefinitions: {
    type: Array,
    default: () => []
  },
  filters: {
    type: Object,
    default: () => ({})
  },
  filterError: {
    type: String,
    default: ''
  },

  items: {
    type: Array,
    required: true
  },
  route: {
    type: String,
    required: true
  }
});

const definitionFor = (key) => props.filterDefinitions.find((definition) => definition.key === key);

function onFilterInput(key, { value, immediate }) {
  emit('update:filters', { ...props.filters, [key]: value });
  emit('filter-change', { key, value, immediate });
}

const navigateToDetail = (id) => {
  router.push(`${props.route}/${id}`);
};

// Función para obtener el valor a mostrar, traduciendo los tipos de solicitud
const getDisplayValue = (item, fieldKey) => {
  const value = item[fieldKey];

  // Si es el campo "type" y tenemos un valor válido, traducirlo
  if (fieldKey === 'type' && value) {
    // Manejar caso especial: other_type + other_action
    if (value === 'other_type') {
      const baseLabel = t(`common.requestTypes.${value}`) || value;
      const actionKey = item?.other_action;
      const actionLabel = actionKey ? (t(`common.requestTypes.${actionKey}`) || actionKey) : '';
      return (baseLabel || value) + (actionLabel ? ' - ' + actionLabel : '');
    }

    return t(`common.requestTypes.${value}`) || value;
  }

  // Si es el campo "status" y tenemos un valor válido, traducirlo
  if (fieldKey === 'status' && value) {
    return t(`common.requestStatus.${value}`) || value;
  }

  if (fieldKey === 'contract_improvement') {
    return item.contract_improvement_accepted_at ? t('contractImprovement.filter.accepted') : '-';
  }

  return value || '-';
};
</script>

<style scoped>
table {
  border-collapse: separate;
  border-spacing: 0 6px;
  table-layout: fixed;
  min-width: 900px;
}

th {
  position: sticky;
  top: 0;
  z-index: 10;
}

tbody tr {
  background: white;
  box-shadow: 0 0 0 1px #385660;
  border-radius: 4px;
}

/* Ajustes específicos para columnas */
th:nth-child(1), td:nth-child(1) { /* Ref. */
  width: 8%;
  padding-right: 0;
}

/* La cabecera sí necesita margen a la derecha: sin él, el filtro de Ref. queda
   pegado al de Fecha, que arranca en el propio borde de su columna. No afecta a
   la etiqueta ni a las celdas de datos, que siguen juntas a propósito. */
thead th:nth-child(1) {
  padding-right: 1rem;
}

th:nth-child(2), td:nth-child(2) { /* Fecha */
  width: 8%;
  padding-left: 0;
}

th:nth-child(3), td:nth-child(3) { /* Tipo */
  width: 12%;
}

th:nth-child(4), td:nth-child(4) { /* Comunidades */
  width: 16%;
}

th:nth-child(5), td:nth-child(5) { /* Dirección */
  width: 16%;
}

th:nth-child(6), td:nth-child(6) { /* Objeto */
  width: 15%;
}

th:nth-child(7), td:nth-child(7) { /* Estado */
  width: 10%;
}
</style>
